import type { EvaluationDetailsBody } from "../api/evaluations/types";

export const CHECKLIST_CATEGORIES = [
    {
        category: "Serve & Solve",
        behaviors: [
            {
                evaluationBehavior: "Greeting",
                displayText: "Greeting",
            },
            {
                evaluationBehavior: "Show Compassion",
                displayText: "Show Compassion",
            },
            {
                evaluationBehavior: "Reassurance",
                displayText: "Reassurance",
            },
            {
                evaluationBehavior: "Set Expectations",
                displayText: "Set Expectations",
            },
            {
                evaluationBehavior: "Confirm Resolution",
                displayText: "Test & Confirm Resolution",
            },
        ],
        saleDetectedBehaviors: [],
    },
    {
        category: "Sell - Smart Offer",
        behaviors: [
            {
                evaluationBehavior: "Transition Statement",
                displayText: "Transition Statement",
            },
            {
                evaluationBehavior: "Discovery Questions",
                displayText: "Discovery Questions",
            },
            {
                evaluationBehavior: "Engagement Questions",
                displayText: "Engagement Questions",
            },
            {
                evaluationBehavior: "Core Benefit - Non Partner Coverage",
                displayText: "Core Benefit - Non Partner Coverage",
            },
            {
                evaluationBehavior: "Core Benefit - Future Purchases",
                displayText: "Core Benefit - Future Purchases",
            },
            {
                evaluationBehavior: "Core Benefit - RRR Explained",
                displayText: "Core Benefit - RRR Explained",
            },
            {
                evaluationBehavior: "Price",
                displayText: "Price",
            },
            {
                evaluationBehavior: "Ask for Sale",
                displayText: "Ask for Sale",
            },
        ],
        saleDetectedBehaviors: [
            {
                evaluationBehavior: "Ask to Send Enrollment Link",
                displayText: "Ask to Send Enrollment Link",
            },
            {
                evaluationBehavior: "Resolve the Customer's Concern",
                displayText: "Resolve the Customer's Concern",
            },
            {
                evaluationBehavior: "Celebrate Enrollment",
                displayText: "Celebrate Enrollment",
            },
        ],
    },
    {
        category: "Sell - Closing",
        behaviors: [
            {
                evaluationBehavior: "Thank & Reinforce",
                displayText: "Thank & Reinforce",
            },
            {
                evaluationBehavior: "Survey",
                displayText: "Survey",
            },
            {
                evaluationBehavior: "Goodbye w/ Rapport",
                displayText: "Goodbye w/ Rapport",
            },
        ],
        saleDetectedBehaviors: [],
    },
];

export function getChecklistCategoryCount({
    category,
    behavior,
    totalBehaviors,
}: {
    category: string;
    behavior: string;
    totalBehaviors: number;
}): { currentIndex: number; total: number } {
    const categoryItem = CHECKLIST_CATEGORIES.find((cat) => cat.category === category);
    if (!categoryItem) {
        return { currentIndex: 0, total: 0 };
    }

    // If there are no sale detected behaviors, return the length of just the behaviors array
    if (categoryItem.saleDetectedBehaviors.length === 0) {
        return {
            currentIndex: categoryItem.behaviors.findIndex((b) => b.evaluationBehavior === behavior) + 1,
            total: categoryItem.behaviors.length,
        };
    }

    const sumOfAvailableBehaviors = CHECKLIST_CATEGORIES.reduce((acc, cur) => {
        return acc + (cur.behaviors.length + cur.saleDetectedBehaviors.length);
    }, 0);
    const salesBehaviorDifference = sumOfAvailableBehaviors - totalBehaviors;
    const total = categoryItem.behaviors.length + categoryItem.saleDetectedBehaviors.length - salesBehaviorDifference;

    // Check if the behavior is in the main behaviors array
    if (categoryItem.behaviors.findIndex((b) => b.evaluationBehavior === behavior) >= 0) {
        return {
            currentIndex: categoryItem.behaviors.findIndex((b) => b.evaluationBehavior === behavior) + 1,
            total,
        };
    }

    // If the behavior is not in the main behaviors array, it must be in the sale detected behaviors array
    // and its index needs to be calculated accordingly
    let salesBehaviorIndex = categoryItem.saleDetectedBehaviors.findIndex((b) => b.evaluationBehavior === behavior) + 1;
    if (salesBehaviorIndex >= categoryItem.saleDetectedBehaviors.length - salesBehaviorDifference) {
        salesBehaviorIndex = categoryItem.saleDetectedBehaviors.length - salesBehaviorDifference;
    }
    return {
        currentIndex: categoryItem.behaviors.length + salesBehaviorIndex,
        total,
    };
}

export function sortBehaviorsByChecklist(evaluationResultsBehaviors: EvaluationDetailsBody[]) {
    const orderedBehaviors: (EvaluationDetailsBody & { category: string })[] = [];
    CHECKLIST_CATEGORIES.forEach((category) => {
        /**
         * Loop through each behavior in the category and
         * match it to the behaviors from evaluationResults
         */
        category.behaviors.forEach((behavior) => {
            const behaviorItem = evaluationResultsBehaviors.find((b) =>
                b.behavior
                    ? behavior.evaluationBehavior.toLocaleLowerCase().includes(b.behavior.toLocaleLowerCase())
                    : false,
            );

            if (behaviorItem) {
                orderedBehaviors.push({
                    ...behaviorItem,
                    category: category.category,
                });
            }
        });

        category.saleDetectedBehaviors.forEach((salesBehavior) => {
            const behaviorItem = evaluationResultsBehaviors.find((b) =>
                b.behavior
                    ? salesBehavior.evaluationBehavior.toLocaleLowerCase().includes(b.behavior.toLocaleLowerCase())
                    : false,
            );

            if (behaviorItem) {
                orderedBehaviors.push({
                    ...behaviorItem,
                    category: category.category,
                });
            }
        });
    });

    return orderedBehaviors;
}
